import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Loader2, TrendingUp, DollarSign, Calendar, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Modal de Relatório Financeiro do Controle Helper
 * Permite selecionar semanas e gerar análise de lucro/prejuízo
 */
const HelperFinancialReportModal = ({ isOpen, onClose, teamId, teamName, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [sharing, setSharing] = useState(false);
  const reportRef = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (isOpen && teamId && teamId !== 'temp') {
      console.log('📊 Modal aberto - Team ID:', teamId);
      loadAvailableWeeks();
    }
  }, [isOpen, teamId]);

  const loadAvailableWeeks = async () => {
    if (!teamId || teamId === 'temp') {
      toast.error('ID da equipe inválido');
      return;
    }
    
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API}/helper-controls/available-weeks/${teamId}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } }
      );
      
      setAvailableWeeks(data.meses || []);
      toast.success(`${data.total_semanas} semanas encontradas`);
    } catch (error) {
      console.error('Erro ao carregar semanas:', error);
      if (error.response?.status === 404) {
        toast.error('Equipe não encontrada. Verifique se a equipe está cadastrada em "Equipes".');
      } else {
        toast.error(`Erro ao carregar semanas: ${error.message}`);
      }
      setAvailableWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleWeek = (weekId) => {
    setSelectedWeeks(prev => 
      prev.includes(weekId) 
        ? prev.filter(id => id !== weekId)
        : [...prev, weekId]
    );
  };

  const selectLastNWeeks = (n) => {
    // Pegar todas as semanas ordenadas por data
    const allWeeks = availableWeeks.flatMap(mes => mes.semanas);
    const lastN = allWeeks.slice(0, n).map(w => w.id);
    setSelectedWeeks(lastN);
  };

  const generateReport = async () => {
    if (selectedWeeks.length === 0) {
      toast.error('Selecione pelo menos uma semana');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API}/helper-controls/financial-report`,
        {
          team_id: teamId,
          selected_week_ids: selectedWeeks
        },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } }
      );
      
      setReportData(data);
      setShowReport(true);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório financeiro');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  // Calcular totais das semanas selecionadas
  const selectedWeeksData = availableWeeks
    .flatMap(mes => mes.semanas)
    .filter(w => selectedWeeks.includes(w.id));
  
  const totalDiasSelecionados = selectedWeeksData.reduce((sum, w) => sum + w.dias_trabalhados, 0);

  const shareReport = async () => {
    if (!reportRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('p', 'mm', imgHeight > 297 ? [imgWidth, imgHeight + 10] : 'a4');
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 5, imgWidth, imgHeight);

      const pdfBlob = pdf.output('blob');
      const fileName = `Relatorio_${reportData.equipe.nome}_${reportData.periodo.data_inicio}_${reportData.periodo.data_fim}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Relatório Financeiro - ${reportData.equipe.nome}`,
          text: `Lucro: ${formatMoney(reportData.lucro.valor)} (${reportData.lucro.margem.toFixed(1)}%)`,
          files: [file],
        });
        toast.success('Relatório compartilhado!');
      } else {
        // Fallback: download direto
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF baixado! (Compartilhamento não suportado neste navegador)');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Erro ao compartilhar:', err);
        toast.error('Erro ao gerar PDF');
      }
    } finally {
      setSharing(false);
    }
  };

  if (showReport && reportData) {
    // Renderizar relatório
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              📊 Relatório Financeiro - {reportData.equipe.nome}
            </DialogTitle>
          </DialogHeader>

          <div ref={reportRef} className="space-y-6">
            {/* Período */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600">Período</div>
                  <div className="font-semibold text-lg">
                    {new Date(reportData.periodo.data_inicio).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {new Date(reportData.periodo.data_fim).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Dias Trabalhados</div>
                  <div className="font-bold text-2xl text-indigo-600">
                    {reportData.periodo.total_dias}
                  </div>
                </div>
              </div>
            </div>

            {/* Receita */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <h3 className="font-bold text-lg text-green-900">Receita (Labor Bills)</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-600">Total Faturado</div>
                  <div className="font-bold text-xl text-green-700">
                    {formatMoney(reportData.receita.total)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Labor Bills</div>
                  <div className="font-semibold text-lg">{reportData.receita.labor_bills_count}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Média/Dia</div>
                  <div className="font-semibold text-lg">
                    {formatMoney(reportData.receita.media_por_dia)}
                  </div>
                </div>
              </div>
            </div>

            {/* Custos */}
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-orange-600" />
                <h3 className="font-bold text-lg text-orange-900">Custos</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Trabalho (Líder + Helpers)</span>
                  <span className="font-semibold">{formatMoney(reportData.custos.trabalho)}</span>
                </div>
                
                {/* Despesas por categoria */}
                <div className="border-t pt-2">
                  <div className="text-sm font-semibold mb-1">Despesas:</div>
                  {Object.entries(reportData.custos.despesas_por_categoria || {}).map(([cat, val]) => (
                    <div key={cat} className="flex justify-between text-sm ml-4">
                      <span>• {cat}</span>
                      <span>{formatMoney(val)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold mt-1 text-sm ml-4">
                    <span>Total Despesas:</span>
                    <span>{formatMoney(reportData.custos.despesas_total)}</span>
                  </div>
                </div>
                
                <div className="border-t pt-2 flex justify-between font-bold text-orange-700">
                  <span>TOTAL CUSTOS:</span>
                  <span>{formatMoney(reportData.custos.total)}</span>
                </div>
              </div>
            </div>

            {/* Lucro */}
            <div className={`p-4 rounded-lg border ${
              reportData.lucro.valor >= 0 
                ? 'bg-emerald-50 border-emerald-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <h3 className="font-bold text-xl mb-2">
                {reportData.lucro.valor >= 0 ? '📈 LUCRO' : '📉 PREJUÍZO'}
              </h3>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-sm text-gray-600">Receita - Custos</div>
                  <div className={`font-bold text-3xl ${
                    reportData.lucro.valor >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {formatMoney(reportData.lucro.valor)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Margem</div>
                  <div className={`font-bold text-2xl ${
                    reportData.lucro.margem >= 50 ? 'text-emerald-600' : 
                    reportData.lucro.margem >= 0 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {reportData.lucro.margem.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Detalhes por Semana */}
            <div>
              <h3 className="font-bold text-lg mb-3">📅 Detalhamento por Semana</h3>
              <div className="space-y-2">
                {reportData.semanas.map(semana => (
                  <div key={semana.semana} className="bg-gray-50 p-3 rounded border">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold">{semana.semana}</div>
                        <div className="text-xs text-gray-600">
                          {new Date(semana.data_inicio).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {new Date(semana.data_fim).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm">
                          <span className="text-gray-600">Dias:</span> <span className="font-semibold">{semana.dias_trabalhados}</span> • 
                          <span className="text-gray-600 ml-2">Bills:</span> <span className="font-semibold">{semana.labor_bills_count}</span>
                        </div>
                        <div className="text-sm mt-1">
                          <span className="text-green-600">+{formatMoney(semana.receita)}</span> - 
                          <span className="text-orange-600 ml-1">{formatMoney(semana.custo_total)}</span> = 
                          <span className={`font-bold ml-1 ${semana.lucro >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatMoney(semana.lucro)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReport(false)}>
              ← Voltar
            </Button>
            <Button
              data-testid="share-report-btn"
              onClick={shareReport}
              disabled={sharing}
              className="bg-green-600 hover:bg-green-700"
            >
              {sharing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4 mr-2" />
              )}
              {sharing ? 'Gerando PDF...' : 'Compartilhar'}
            </Button>
            <Button onClick={onClose}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Modal de seleção de semanas
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            📊 Relatório Financeiro - {teamName}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {!loading && (
          <div className="space-y-4">
            {/* Botão para carregar semanas */}
            {availableWeeks.length === 0 && (
              <Button 
                onClick={loadAvailableWeeks}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                📅 Carregar Semanas Disponíveis
              </Button>
            )}
            
            {/* Atalhos - só mostrar se tem semanas */}
            {availableWeeks.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                <Badge className="text-xs">⚡ Atalhos:</Badge>
                <Button size="sm" variant="outline" onClick={() => selectLastNWeeks(2)}>
                  Quinzenal (2 sem)
                </Button>
                <Button size="sm" variant="outline" onClick={() => selectLastNWeeks(4)}>
                  Mensal (4 sem)
                </Button>
                <Button size="sm" variant="outline" onClick={() => selectLastNWeeks(12)}>
                  Trimestral (12 sem)
                </Button>
                <Button size="sm" variant="outline" onClick={() => selectLastNWeeks(24)}>
                  Semestral (24 sem)
                </Button>
              </div>
            )}

            {/* Lista de semanas por mês */}
            <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
              {availableWeeks.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  Nenhuma semana disponível
                </div>
              ) : (
                availableWeeks.map(mes => (
                  <div key={mes.mes_ano} className="mb-4">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2">
                      🗓️ {mes.mes_nome}
                    </h3>
                    <div className="space-y-2 ml-4">
                      {mes.semanas.map(semana => (
                        <label
                          key={semana.id}
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedWeeks.includes(semana.id)}
                            onCheckedChange={() => toggleWeek(semana.id)}
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{semana.semana}</span>
                              <div className="text-xs text-gray-600">
                                {semana.dias_trabalhados} dias • {formatMoney(semana.valor_final)}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(semana.data_inicio).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {new Date(semana.data_fim).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Preview */}
            {selectedWeeks.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-blue-900">
                    ✓ {selectedWeeks.length} semanas selecionadas
                  </span>
                  <span className="text-blue-700">
                    {totalDiasSelecionados} dias no total
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={generateReport}
            disabled={loading || selectedWeeks.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                📊 Gerar Relatório
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HelperFinancialReportModal;
